import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  HelpCircle,
  BookOpen,
  MessageCircleQuestion,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  Settings,
  ChevronRight,
  FileText,
  Sparkles,
  Search,
  Eye,
  EyeOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HelpCenterPage = () => {
  usePageTitle("Help Center");
  const { token, user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("instructions");
  
  // Admin management state
  const [adminArticles, setAdminArticles] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adminRoleFilter, setAdminRoleFilter] = useState("all");
  const [adminCategoryFilter, setAdminCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const isAdmin = user?.role === "super_admin";
  
  // Form state for new/edit article
  const [articleForm, setArticleForm] = useState({
    role: "error_manager",
    category: "instructions",
    title: "",
    content: "",
    order: 0,
    is_active: true
  });

  const fetchArticles = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/help`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (error) {
      toast.error("Failed to load help articles");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAdminArticles = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const params = new URLSearchParams();
      if (adminRoleFilter !== "all") params.append("role_filter", adminRoleFilter);
      if (adminCategoryFilter !== "all") params.append("category_filter", adminCategoryFilter);
      
      const response = await fetch(`${BACKEND_URL}/api/help/admin?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setAdminArticles(data.articles || []);
    } catch (error) {
      toast.error("Failed to load articles for admin");
    }
  }, [token, isAdmin, adminRoleFilter, adminCategoryFilter]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    if (showAdminPanel) {
      fetchAdminArticles();
    }
  }, [showAdminPanel, fetchAdminArticles]);

  const handleSeedDefault = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/help/seed-default`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      toast.success(data.message);
      fetchArticles();
      fetchAdminArticles();
    } catch (error) {
      toast.error("Failed to seed default articles");
    }
  };

  const handleOpenCreate = () => {
    setEditingArticle(null);
    setArticleForm({
      role: "error_manager",
      category: "instructions",
      title: "",
      content: "",
      order: 0,
      is_active: true
    });
    setShowArticleDialog(true);
  };

  const handleOpenEdit = (article) => {
    setEditingArticle(article);
    setArticleForm({
      role: article.role,
      category: article.category,
      title: article.title,
      content: article.content,
      order: article.order,
      is_active: article.is_active
    });
    setShowArticleDialog(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    setSaving(true);
    try {
      const url = editingArticle 
        ? `${BACKEND_URL}/api/help/${editingArticle.id}`
        : `${BACKEND_URL}/api/help`;
      
      const response = await fetch(url, {
        method: editingArticle ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(articleForm)
      });
      
      if (response.ok) {
        toast.success(editingArticle ? "Article updated" : "Article created");
        setShowArticleDialog(false);
        fetchArticles();
        fetchAdminArticles();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save article");
      }
    } catch (error) {
      toast.error("Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  // Trigger delete confirmation
  const triggerDeleteArticle = (article) => {
    setDeleteTarget({ id: article.id, name: article.title });
    setShowDeleteDialog(true);
  };

  // Execute delete after password confirmation
  const executeDeleteArticle = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/help/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Article deleted");
        fetchArticles();
        fetchAdminArticles();
      } else {
        toast.error("Failed to delete article");
      }
    } catch (error) {
      toast.error("Failed to delete article");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleToggleActive = async (article) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/help/${article.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !article.is_active })
      });
      
      if (response.ok) {
        toast.success(article.is_active ? "Article hidden" : "Article visible");
        fetchArticles();
        fetchAdminArticles();
      }
    } catch (error) {
      toast.error("Failed to update article");
    }
  };

  // Filter articles by category
  const instructionArticles = articles.filter(a => a.category === "instructions");
  const faqArticles = articles.filter(a => a.category === "faq");
  
  // Filter admin articles by search
  const filteredAdminArticles = adminArticles.filter(a => 
    searchQuery === "" || 
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get role label
  const getRoleLabel = (role) => {
    switch (role) {
      case "error_manager": return "Error Manager";
      case "uploader": return "Uploader";
      default: return role;
    }
  };

  // Get role badge color
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "error_manager": return "bg-purple-100 text-purple-700 border-purple-300";
      case "uploader": return "bg-blue-100 text-blue-700 border-blue-300";
      default: return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="help-center-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Help Center</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isAdmin ? "Instructions and FAQs for all roles" : `Instructions and FAQs for ${getRoleLabel(user?.role)}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchArticles}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          
          {isAdmin && (
            <Button
              size="sm"
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              className={`gap-2 ${showAdminPanel ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
              data-testid="admin-panel-toggle"
            >
              <Settings className="w-4 h-4" />
              {showAdminPanel ? "View Mode" : "Manage Articles"}
            </Button>
          )}
        </div>
      </div>

      {/* Admin Panel */}
      {isAdmin && showAdminPanel && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-500" />
              Article Management
            </h2>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSeedDefault}
                className="gap-2"
                data-testid="seed-default-btn"
              >
                <Sparkles className="w-4 h-4" />
                Seed Default
              </Button>
              <Button
                size="sm"
                onClick={handleOpenCreate}
                className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-article-btn"
              >
                <Plus className="w-4 h-4" />
                New Article
              </Button>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Role:</Label>
              <Select value={adminRoleFilter} onValueChange={setAdminRoleFilter}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="error_manager">Error Manager</SelectItem>
                  <SelectItem value="uploader">Uploader</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm">Category:</Label>
              <Select value={adminCategoryFilter} onValueChange={setAdminCategoryFilter}>
                <SelectTrigger className="w-40 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="instructions">Instructions</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2 flex-1 max-w-xs">
              <Search className="w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8"
              />
            </div>
            
            <Badge variant="outline" className="text-xs">
              {filteredAdminArticles.length} articles
            </Badge>
          </div>
          
          {/* Admin Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="w-32">Role</TableHead>
                  <TableHead className="w-32">Category</TableHead>
                  <TableHead className="w-20">Order</TableHead>
                  <TableHead className="w-20">Status</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAdminArticles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No articles found. Click &quot;Seed Default&quot; to add default articles.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAdminArticles.map((article, index) => (
                    <TableRow key={article.id} className={!article.is_active ? "opacity-50" : ""}>
                      <TableCell className="text-slate-500">{index + 1}</TableCell>
                      <TableCell className="font-medium">{article.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getRoleBadgeClass(article.role)}>
                          {getRoleLabel(article.role)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={article.category === "instructions" ? "bg-green-50 text-green-700 border-green-300" : "bg-amber-50 text-amber-700 border-amber-300"}>
                          {article.category === "instructions" ? "Instructions" : "FAQ"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{article.order}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleActive(article)}
                          className={`p-1 rounded ${article.is_active ? "text-green-600 hover:bg-green-50" : "text-slate-400 hover:bg-slate-100"}`}
                          title={article.is_active ? "Click to hide" : "Click to show"}
                        >
                          {article.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(article)}
                            className="h-7 w-7 p-0"
                            data-testid={`edit-article-${article.id}`}
                          >
                            <Edit3 className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => triggerDeleteArticle(article)}
                            className="h-7 w-7 p-0"
                            data-testid={`delete-article-${article.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* User View - Help Content */}
      {!showAdminPanel && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : articles.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Help Articles Available</h3>
              <p className="text-slate-500 mb-4">Help content has not been configured yet.</p>
              {isAdmin && (
                <Button onClick={handleSeedDefault} className="gap-2">
                  <Sparkles className="w-4 h-4" />
                  Add Default Articles
                </Button>
              )}
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="bg-slate-100 p-1">
                <TabsTrigger value="instructions" className="gap-2 data-[state=active]:bg-white">
                  <BookOpen className="w-4 h-4" />
                  Instructions
                  {instructionArticles.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {instructionArticles.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="faq" className="gap-2 data-[state=active]:bg-white">
                  <MessageCircleQuestion className="w-4 h-4" />
                  FAQ
                  {faqArticles.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {faqArticles.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="instructions" className="space-y-4">
                {instructionArticles.length === 0 ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    No instructions available
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="space-y-2">
                    {instructionArticles.map((article, index) => (
                      <AccordionItem
                        key={article.id}
                        value={article.id}
                        className="bg-white border border-slate-200 rounded-lg px-4 overflow-hidden"
                      >
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex items-center gap-3 text-left">
                            <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="font-medium text-slate-800">{article.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4">
                          <div 
                            className="prose prose-sm max-w-none text-slate-600 pl-10"
                            dangerouslySetInnerHTML={{ __html: article.content }}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </TabsContent>

              <TabsContent value="faq" className="space-y-4">
                {faqArticles.length === 0 ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-slate-500">
                    <MessageCircleQuestion className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    No FAQs available
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="space-y-2">
                    {faqArticles.map((article) => (
                      <AccordionItem
                        key={article.id}
                        value={article.id}
                        className="bg-white border border-slate-200 rounded-lg px-4 overflow-hidden"
                      >
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex items-center gap-3 text-left">
                            <MessageCircleQuestion className="w-5 h-5 text-amber-500 flex-shrink-0" />
                            <span className="font-medium text-slate-800">{article.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4">
                          <div 
                            className="prose prose-sm max-w-none text-slate-600 pl-8"
                            dangerouslySetInnerHTML={{ __html: article.content }}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </TabsContent>
            </Tabs>
          )}
        </>
      )}

      {/* Create/Edit Article Dialog */}
      <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingArticle ? <Edit3 className="w-5 h-5 text-blue-500" /> : <Plus className="w-5 h-5 text-green-500" />}
              {editingArticle ? "Edit Article" : "Create New Article"}
            </DialogTitle>
            <DialogDescription>
              {editingArticle ? "Update the article details below." : "Fill in the details for the new help article."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Role</Label>
                <Select 
                  value={articleForm.role} 
                  onValueChange={(v) => setArticleForm(prev => ({ ...prev, role: v }))}
                >
                  <SelectTrigger data-testid="article-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="error_manager">Error Manager</SelectItem>
                    <SelectItem value="uploader">Uploader</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={articleForm.category} 
                  onValueChange={(v) => setArticleForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger data-testid="article-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instructions">Instructions</SelectItem>
                    <SelectItem value="faq">FAQ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={articleForm.title}
                onChange={(e) => setArticleForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter article title"
                data-testid="article-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Content</Label>
              <RichTextEditor
                content={articleForm.content}
                onChange={(html) => setArticleForm(prev => ({ ...prev, content: html }))}
                placeholder="Write your article content here..."
                minHeight="200px"
                data-testid="article-content-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={articleForm.order}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  data-testid="article-order-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={articleForm.is_active ? "active" : "inactive"} 
                  onValueChange={(v) => setArticleForm(prev => ({ ...prev, is_active: v === "active" }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (Visible)</SelectItem>
                    <SelectItem value="inactive">Inactive (Hidden)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArticleDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveArticle} disabled={saving} data-testid="save-article-btn">
              {saving ? "Saving..." : editingArticle ? "Update Article" : "Create Article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Help Article"
        description="This will permanently delete this help article."
        itemName={deleteTarget?.name}
        onConfirm={executeDeleteArticle}
        loading={deleting}
      />
    </div>
  );
};

export default HelpCenterPage;
